import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DataTable,
  ExportButtons,
  FilterBar,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import {
  admHistoryPeriods,
  admHistoryRunSnapshot,
  admHistorySetVisibility,
  admHistorySnapshots,
} from "@/lib/history-admin.functions";
import { MONTH_SLUGS } from "@/lib/history";
import { formatHeat } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/history")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/history",
      "Board history | Control room",
      "Monthly board snapshots.",
    ),
  component: AdminHistory,
});

type SnapshotRow = {
  id: string;
  year: number;
  month: number;
  category_slug: string;
  category_name: string;
  parent_name: string | null;
  entry_count: number;
  total_heat_cents: number;
  is_public: boolean;
  hidden_reason: string | null;
  finalized_at: string | null;
};

type PeriodRow = {
  id: string;
  year: number;
  month: number;
  status: string;
  board_count: number;
  entry_count: number;
  total_heat_cents: number;
  finalized_at: string | null;
};

const MONTH_OPTIONS = [
  { value: "all", label: "All months" },
  ...MONTH_SLUGS.map((slug, i) => ({
    value: String(i + 1),
    label: slug.charAt(0).toUpperCase() + slug.slice(1),
  })),
];

function AdminHistory() {
  const qc = useQueryClient();
  const loadPeriods = useServerFn(admHistoryPeriods);
  const loadSnapshots = useServerFn(admHistorySnapshots);
  const runSnapshot = useServerFn(admHistoryRunSnapshot);
  const setVisibility = useServerFn(admHistorySetVisibility);

  const now = new Date();
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [runYear, setRunYear] = useState(String(now.getUTCFullYear()));
  const [runMonth, setRunMonth] = useState(String(now.getUTCMonth() || 12));

  const periods = useQuery({
    queryKey: ["adm-history-periods"],
    queryFn: () => loadPeriods({ data: undefined }) as Promise<PeriodRow[]>,
  });

  const snapshots = useQuery({
    queryKey: ["adm-history-snapshots", search, year, month],
    placeholderData: keepPreviousData,
    queryFn: () =>
      loadSnapshots({
        data: {
          ...(search ? { search } : {}),
          ...(year !== "all" ? { year: Number(year) } : {}),
          ...(month !== "all" ? { month: Number(month) } : {}),
        },
      }) as Promise<SnapshotRow[]>,
  });

  const runMutation = useMutation({
    mutationFn: (force: boolean) =>
      runSnapshot({ data: { year: Number(runYear), month: Number(runMonth), force } }),
    onSuccess: (res) => {
      toast.success(`Snapshot finished — ${res.boards} boards recorded`);
      void qc.invalidateQueries({ queryKey: ["adm-history-periods"] });
      void qc.invalidateQueries({ queryKey: ["adm-history-snapshots"] });
    },
    onError: (e: Error) => toast.error(e.message || "Snapshot failed"),
  });

  const visibilityMutation = useMutation({
    mutationFn: (v: { snapshotId: string; visible: boolean }) =>
      setVisibility({
        data: {
          snapshotId: v.snapshotId,
          visible: v.visible,
          ...(v.visible ? {} : { reason: "Hidden from the control room" }),
        },
      }),
    onSuccess: () => {
      toast.success("Visibility updated");
      void qc.invalidateQueries({ queryKey: ["adm-history-snapshots"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not update visibility"),
  });

  const rows = snapshots.data ?? [];

  const years = useMemo(() => {
    const set = new Set<number>((periods.data ?? []).map((p) => p.year));
    return [
      { value: "all", label: "All years" },
      ...[...set].sort((a, b) => b - a).map((y) => ({ value: String(y), label: String(y) })),
    ];
  }, [periods.data]);

  const exportRows = rows.map((r) => ({
    year: r.year,
    month: r.month,
    board: r.category_name,
    slug: r.category_slug,
    entries: r.entry_count,
    heat: formatHeat(r.total_heat_cents),
    visible: r.is_public ? "yes" : "no",
    finalized_at: r.finalized_at ?? "",
  }));

  const totalEntries = (periods.data ?? []).reduce((s, p) => s + p.entry_count, 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Recorded months" value={String(periods.data?.length ?? 0)} />
        <StatCard label="Board snapshots" value={String(rows.length)} />
        <StatCard label="Ranked entries" value={String(totalEntries)} />
      </div>

      <Section title="Run a snapshot" description="Idempotent — re-running a month never duplicates recorded positions.">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-muted-foreground">
            Year
            <Input
              value={runYear}
              onChange={(e) => setRunYear(e.target.value)}
              className="mt-1 w-28"
              inputMode="numeric"
            />
          </label>
          <div className="w-40">
            <SelectFilter
              label="Month"
              value={runMonth}
              onChange={setRunMonth}
              options={MONTH_OPTIONS.filter((o) => o.value !== "all")}
            />
          </div>
          <Button
            variant="hot"
            disabled={runMutation.isPending}
            onClick={() => runMutation.mutate(false)}
          >
            {runMutation.isPending ? "Running…" : "Create missing snapshot"}
          </Button>
          <Button
            variant="outline"
            disabled={runMutation.isPending}
            onClick={() => {
              if (confirm("Regenerate this month from live data? Existing recorded positions for this month will be rebuilt.")) {
                runMutation.mutate(true);
              }
            }}
          >
            Force regenerate
          </Button>
        </div>
      </Section>

      <Section title="Board snapshots">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search board…" />
          <SelectFilter label="Year" value={year} onChange={setYear} options={years} />
          <SelectFilter label="Month" value={month} onChange={setMonth} options={MONTH_OPTIONS} />
          <ExportButtons rows={exportRows} filename="board-history" title="Board history" />
        </FilterBar>

        <DataTable<SnapshotRow>
          rows={rows}
          rowKey={(r) => r.id}
          empty={snapshots.isFetching ? "Loading…" : "No snapshots recorded yet."}
          columns={[
            { key: "period", header: "Period", cell: (r) => `${MONTH_SLUGS[r.month - 1]} ${r.year}` },
            {
              key: "board",
              header: "Board",
              cell: (r) => (
                <span>
                  <span className="font-medium">{r.category_name}</span>
                  {r.parent_name && (
                    <span className="block text-xs text-muted-foreground">{r.parent_name}</span>
                  )}
                </span>
              ),
            },
            { key: "entries", header: "Entries", cell: (r) => r.entry_count },
            { key: "heat", header: "Heat", cell: (r) => formatHeat(r.total_heat_cents) },
            {
              key: "visible",
              header: "Public",
              cell: (r) => (r.is_public ? "Visible" : (r.hidden_reason ?? "Hidden")),
            },
            {
              key: "actions",
              header: "",
              cell: (r) => (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={visibilityMutation.isPending}
                  onClick={() =>
                    visibilityMutation.mutate({ snapshotId: r.id, visible: !r.is_public })
                  }
                >
                  {r.is_public ? "Hide" : "Publish"}
                </Button>
              ),
            },
          ]}
        />
      </Section>
    </div>
  );
}
